import { isStandalonePwa, isTouchDevice } from './mobilePwa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let activeDownloadPromise = null;

export function videoDownloadFileName(title) {
  const base = (title || 'video').replace(/[^\w\s\-]/gi, '').trim().replace(/\s+/g, '_') || 'video';
  return `${base.slice(0, 80)}.mp4`;
}

export function videoDownloadApiUrl(contentId, engagementSource = 'main') {
  if (engagementSource === 'spille') {
    return `${API}/spille/contents/${contentId}/download-video`;
  }
  return `${API}/contents/${contentId}/download-video`;
}

function videoDownloadLinkApiUrl(contentId, engagementSource = 'main') {
  if (engagementSource === 'spille') {
    return `${API}/spille/contents/${contentId}/download-video-link`;
  }
  return `${API}/contents/${contentId}/download-video-link`;
}

export function needsMobileVideoDownloadFlow() {
  return isTouchDevice() || isStandalonePwa();
}

async function fetchVideoBlob(downloadUrl, onProgress) {
  const response = await fetch(downloadUrl, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Download fallito (${response.status})`);
  }

  const total = Number(response.headers.get('content-length') || 0);
  if (!response.body) {
    const blob = await response.blob();
    onProgress?.(1, blob.size, total || blob.size);
    return blob;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress?.(total ? received / total : null, received, total);
  }

  const blob = new Blob(chunks, { type: response.headers.get('content-type') || 'video/mp4' });
  onProgress?.(1, blob.size, total || blob.size);
  return blob;
}

async function shareOrSaveBlob(blob, fileName) {
  const mimeType = blob.type || 'video/mp4';
  const file = new File([blob], fileName, { type: mimeType });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: fileName });
      return;
    } catch (shareErr) {
      if (shareErr?.name === 'AbortError') return;
    }
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

async function openDirectVideoLink(contentId, engagementSource) {
  const response = await fetch(videoDownloadLinkApiUrl(contentId, engagementSource), {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Link download non disponibile');
  const data = await response.json();
  if (!data?.url) throw new Error('Link download non disponibile');

  const opened = window.open(data.url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    window.location.assign(data.url);
  }
}

async function downloadOnMobile(content, engagementSource, onProgress) {
  const fileName = videoDownloadFileName(content.title);
  const downloadUrl = videoDownloadApiUrl(content.id, engagementSource);

  try {
    const blob = await fetchVideoBlob(downloadUrl, onProgress);
    await shareOrSaveBlob(blob, fileName);
    return { mode: 'saved' };
  } catch (primaryErr) {
    try {
      await openDirectVideoLink(content.id, engagementSource);
      return {
        mode: 'opened_external',
        message:
          'Video aperto nel browser. Per salvarlo: tocca Condividi e poi Salva video o Salva in File.',
      };
    } catch {
      throw primaryErr;
    }
  }
}

function downloadOnDesktop(content, engagementSource) {
  const downloadUrl = videoDownloadApiUrl(content.id, engagementSource);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
  iframe.src = downloadUrl;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), 10 * 60 * 1000);
}

/** Scarica un video ScreenPal tramite proxy backend autenticato. */
export async function downloadVideoContent(content, { engagementSource = 'main', onProgress } = {}) {
  if (!content?.id) throw new Error('Contenuto non valido');

  if (activeDownloadPromise) return activeDownloadPromise;

  activeDownloadPromise = (async () => {
    if (needsMobileVideoDownloadFlow()) {
      await downloadOnMobile(content, engagementSource, onProgress);
      return;
    }
    downloadOnDesktop(content, engagementSource);
  })();

  try {
    return await activeDownloadPromise;
  } finally {
    activeDownloadPromise = null;
  }
}
